// MailingListForm.jsx
import { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { supabase } from "../supabaseClient";

const fieldStyles = {
    "& .MuiInputLabel-root": {
        color: "rgba(255,255,255,.5)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#fff",
    },
    "& .MuiOutlinedInput-root": {
        color: "#fff",
        bgcolor: "rgba(255,255,255,.035)",
        borderRadius: 0,
        "& fieldset": {
            borderColor: "rgba(255,255,255,.22)",
        },
        "&:hover fieldset": {
            borderColor: "rgba(255,255,255,.45)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#fff",
            borderWidth: 1,
        },
    },
    "& input:-webkit-autofill": {
        WebkitTextFillColor: "#fff",
        WebkitBoxShadow: "0 0 0 100px #151515 inset",
    },
};

export default function MailingListForm({
    listSlug,
    eyebrow = "Lista de avisos",
    title = "Mantente al día",
    description = "Déjanos tu nombre y correo para recibir novedades.",
    buttonText = "Apuntarme",
    successMessage = "Perfecto. Te avisaré cuando haya novedades.",
}) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!name.trim()) {
            setResult({
                severity: "error",
                text: "Escribe tu nombre.",
            });
            return;
        }

        if (!listSlug) {
            setResult({
                severity: "error",
                text: "Esta lista no está disponible.",
            });
            return;
        }

        setLoading(true);
        setResult(null);

        try {
            const { error } = await supabase.rpc("subscribe_to_mailing_list", {
                p_list: listSlug,
                p_name: name,
                p_email: email,
            });

            if (error) throw error;

            setName("");
            setEmail("");
            setResult({
                severity: "success",
                text: successMessage,
            });
        } catch (error) {
            console.error("Could not subscribe to mailing list.", error);
            setResult({
                severity: "error",
                text: "No hemos podido guardar tus datos. Inténtalo de nuevo.",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                width: "100%",
                maxWidth: 900,
                mx: "auto",
                mt: { xs: 9, md: 12 },
                pt: { xs: 5, md: 6 },
                borderTop: "1px solid rgba(255,255,255,.15)",
                textAlign: "center",
            }}
        >
            <Typography
                sx={{
                    mb: 1.5,
                    fontSize: ".75rem",
                    letterSpacing: ".22em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,.5)",
                }}
            >
                {eyebrow}
            </Typography>

            <Typography
                component="h2"
                sx={{
                    fontSize: { xs: "1.7rem", md: "2.1rem" },
                    fontWeight: 600,
                    lineHeight: 1.2,
                    letterSpacing: "-.025em",
                }}
            >
                {title}
            </Typography>

            {description && (
                <Typography
                    sx={{
                        maxWidth: 560,
                        mx: "auto",
                        mt: 1.5,
                        mb: 4,
                        color: "rgba(255,255,255,.55)",
                        lineHeight: 1.7,
                    }}
                >
                    {description}
                </Typography>
            )}

            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                sx={{ alignItems: "stretch" }}
            >
                <TextField
                    label="Nombre"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    inputProps={{ maxLength: 100 }}
                    autoComplete="name"
                    required
                    fullWidth
                    sx={fieldStyles}
                />

                <TextField
                    label="Correo electrónico"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    inputProps={{ maxLength: 320 }}
                    autoComplete="email"
                    required
                    fullWidth
                    sx={fieldStyles}
                />

                <Button
                    type="submit"
                    variant="outlined"
                    disabled={loading}
                    sx={{
                        minWidth: { md: 150 },
                        minHeight: 56,
                        px: 3.5,
                        color: "#fff",
                        borderColor: "rgba(255,255,255,.6)",
                        borderRadius: 0,
                        fontSize: ".75rem",
                        letterSpacing: ".14em",
                        whiteSpace: "nowrap",
                        "&:hover": {
                            bgcolor: "rgba(255,255,255,.06)",
                            borderColor: "#fff",
                        },
                        "&.Mui-disabled": {
                            color: "rgba(255,255,255,.35)",
                            borderColor: "rgba(255,255,255,.15)",
                        },
                    }}
                >
                    {loading ? "Guardando..." : buttonText}
                </Button>
            </Stack>

            {result && (
                <Alert
                    severity={result.severity}
                    variant="outlined"
                    aria-live="polite"
                    sx={{
                        mt: 2,
                        borderRadius: 0,
                        bgcolor: "rgba(0,0,0,.25)",
                        color:
                            result.severity === "success"
                                ? "rgba(255,255,255,.85)"
                                : "#ffb4ab",
                        borderColor:
                            result.severity === "success"
                                ? "rgba(255,255,255,.2)"
                                : "rgba(255,180,171,.4)",
                        "& .MuiAlert-icon": {
                            color: "inherit",
                        },
                    }}
                >
                    {result.text}
                </Alert>
            )}
        </Box>
    );
}
